<section
  class="flex h-full w-full flex-col overflow-hidden bg-white"
  role="dialog"
  [attr.aria-label]="'csv_field_mapping_title' | translate"
>
  <header class="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
    <div class="min-w-0 flex-1">
      <h2 class="m-0 text-lg font-bold text-gray-900">{{ 'csv_field_mapping_title' | translate }}</h2>
      <p class="m-0 mt-1 text-sm text-gray-500">{{ 'board_v3_import_fields_description' | translate }}</p>
    </div>
  </header>

  <div class="crm-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">
    @if (!hasFile) {
      <div class="flex h-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-16 text-center">
        <app-icon class="text-4xl text-gray-300">upload_file</app-icon>
        <p class="m-0 text-sm text-gray-500">{{ 'board_v3_import_upload_hint' | translate }}</p>
        <app-button (onChangeClick)="onUploadClick()">
          <span class="flex items-center gap-1"><app-icon>upload</app-icon>{{ 'upload_csv' | translate }}</span>
        </app-button>
      </div>
    } @else {
      <div class="mb-5 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
        <div class="flex min-w-0 items-center gap-2">
          <app-icon class="shrink-0 text-gray-400">description</app-icon>
          <span class="truncate text-sm font-medium text-gray-700">{{ fileName }}</span>
        </div>
        <button
          type="button"
          class="shrink-0 text-sm font-medium text-blue-600 hover:underline"
          [disabled]="isImporting"
          (click)="onUploadClick()"
        >
          {{ 'board_v3_import_change_file' | translate }}
        </button>
      </div>

      <div class="mb-5">
        <label class="mb-1 block text-sm font-medium text-gray-700">{{ 'import_to_column' | translate }}</label>
        <app-select
          [(ngModel)]="selectedColumnId"
          [options]="columnOptions"
          optionLabel="name"
          optionValue="id"
          [fluid]="true"
          [disabled]="isImporting"
          [placeholder]="'select_column' | translate"
          [appendTo]="'body'"
        />
      </div>

      <div class="mb-2 flex items-center justify-between gap-3">
        <div class="text-sm font-semibold text-gray-800">{{ 'board_v3_import_fields_title' | translate }}</div>
        <div class="text-xs text-gray-400">
          {{ 'board_v3_import_from_csv' | translate }}: {{ csvMappedCount }} · {{ 'board_v3_import_manual' | translate }}: {{ manualCount }}
        </div>
      </div>

      @if (isLoadingFields) {
        <div class="rounded-xl border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
          {{ 'loading' | translate }}
        </div>
      } @else if (!rows.length) {
        <div class="rounded-xl border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
          {{ 'board_v3_import_no_fields' | translate }}
        </div>
      } @else {
        <div class="flex flex-col gap-2">
          @for (row of rows; track row.key) {
            <div class="rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm">
              <div class="flex items-start gap-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5">
                    <span class="truncate text-sm font-medium text-gray-800">{{ row.field.name | translate }}</span>
                    @if (row.field.is_required) {
                      <span class="text-sm text-red-500">*</span>
                    }
                  </div>
                  <div class="truncate text-xs text-gray-400">
                    {{ (row.field.is_system ? 'board_v3_import_standard_fields' : 'board_v3_import_custom_fields') | translate }}
                  </div>
                </div>

                <div class="flex shrink-0 items-center gap-1 rounded-lg bg-gray-100 p-0.5">
                  <button
                    type="button"
                    class="rounded-md! px-2 py-1 text-xs font-medium transition-colors"
                    [class]="row.mode === 'csv' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                    [disabled]="isImporting"
                    (click)="onModeChange(row, 'csv')"
                  >
                    {{ 'board_v3_import_from_csv' | translate }}
                  </button>
                  <button
                    type="button"
                    class="rounded-md! px-2 py-1 text-xs font-medium transition-colors"
                    [class]="row.mode === 'manual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                    [disabled]="isImporting"
                    (click)="onModeChange(row, 'manual')"
                  >
                    {{ 'board_v3_import_manual' | translate }}
                  </button>
                  <button
                    type="button"
                    class="rounded-md! px-2 py-1 text-xs font-medium transition-colors"
                    [class]="row.mode === 'none' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                    [disabled]="isImporting"
                    (click)="onModeChange(row, 'none')"
                  >
                    {{ 'board_v3_import_skip' | translate }}
                  </button>
                </div>
              </div>

              @if (row.mode === 'csv') {
                <div class="mt-3">
                  <app-select
                    [(ngModel)]="row.csvColumn"
                    [options]="csvColumnOptions"
                    optionLabel="label"
                    optionValue="value"
                    [showClear]="true"
                    [searchInInput]="true"
                    [disabled]="isImporting"
                    [placeholder]="'board_v3_import_select_csv_column' | translate"
                    [appendTo]="'body'"
                  />
                  @if (getCsvSample(row); as sample) {
                    <div class="mt-1 truncate text-xs text-gray-400">{{ sample }}</div>
                  }
                  @if (isCsvColumnUsed(row)) {
                    <div class="mt-1 text-xs text-amber-600">{{ 'field_already_mapped' | translate }}</div>
                  }
                </div>
              }

              @if (row.mode === 'manual') {
                <div class="mt-3">
                  @if (isManagerField(row.field)) {
                    <app-select
                      [formControl]="row.control"
                      [options]="managerOptions"
                      optionLabel="name"
                      optionValue="id"
                      [isAvatar]="true"
                      [fluid]="true"
                      [showClear]="true"
                      [disabled]="isImporting"
                      [placeholder]="'select_manager' | translate"
                      [appendTo]="'body'"
                    />
                  } @else {
                    <app-custom-field-control [field]="row.field" [control]="row.control" [showLabel]="false" />
                  }
                </div>
              }
            </div>
          }
        </div>
      }

      @if (previewRows.length) {
        <div class="mt-6">
          <div class="mb-2 text-sm font-semibold text-gray-800">{{ 'preview_import' | translate }}</div>
          <div class="overflow-x-auto rounded-xl border border-gray-200">
            <table class="min-w-full divide-y divide-gray-100 text-sm">
              <thead class="bg-gray-50">
                <tr>
                  @for (column of csvColumns; track column.name) {
                    <th class="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500">{{ column.name }}</th>
                  }
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                @for (row of previewRows; track $index) {
                  <tr>
                    @for (column of csvColumns; track column.name) {
                      <td class="max-w-[180px] truncate whitespace-nowrap px-3 py-2 text-gray-700">{{ row[column.name] }}</td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }
    }
  </div>

  <footer class="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
    <app-button [btnType]="'stroked'" [disabled]="isImporting" (onChangeClick)="onCancel()">{{ 'cancel' | translate }}</app-button>
    @if (hasFile) {
      <app-button [disabled]="!canImport" (onChangeClick)="onImport()">
        <span class="flex items-center gap-1"><app-icon>upload</app-icon>{{ 'board_v3_settings_import_cards' | translate }}</span>
      </app-button>
    }
  </footer>
</section>
